import { createBrowserRouter } from 'react-router';
import RootLayout from '@/app/RootLayout';
import ForgotPasswordPage from '@/auth/pages/ForgotPasswordPage';
import LoginPage from '@/auth/pages/LoginPage';
import LogoutPage from '@/auth/pages/LogoutPage';
import RegisterPage from '@/auth/pages/RegisterPage';
import ResetPasswordPage from '@/auth/pages/ResetPasswordPage';
import { redirectSignedInUser } from '@/auth/redirectSignedInUser';
import SecureRoute from '@/auth/SecureRoute';
import ErrorAlert from '@/components/errors/ErrorAlert';
import ErrorPage from '@/error-page';

// main pages
import EmailValidationPage from '@/routes/EmailValidationPage';
import HelpPage from '@/routes/HelpPage';
import InvitationPage from '@/routes/invitation/InvitationPage';
import NoSpacePage from '@/routes/noSpace/NoSpacePage';

import { AssistantRouteGate } from './routes/assistant/AssistantRouteGate';

// settings

import { lazy } from 'react';

const SiteLayout = lazy(() => import('@/app/SiteLayout'));

const securePages = () => import('@/routes/groups/securePages');
const AccountsPage = lazy(() => securePages().then(m => ({ default: m.AccountsPage })));
const AssistantPage = lazy(() => securePages().then(m => ({ default: m.AssistantPage })));
const BudgetsPage = lazy(() => securePages().then(m => ({ default: m.BudgetsPage })));
const CashflowReportPage = lazy(() => securePages().then(m => ({ default: m.CashflowReportPage })));
const DashboardPage = lazy(() => securePages().then(m => ({ default: m.DashboardPage })));
const InstitutionsPage = lazy(() => securePages().then(m => ({ default: m.InstitutionsPage })));
const InvestmentsPage = lazy(() => securePages().then(m => ({ default: m.InvestmentsPage })));
const ManageAccountsPage = lazy(() => securePages().then(m => ({ default: m.ManageAccountsPage })));
const ManageCategoriesPage = lazy(() => securePages().then(m => ({ default: m.ManageCategoriesPage })));
const ManageRulesPage = lazy(() => securePages().then(m => ({ default: m.ManageRulesPage })));
const ManageSpacesPage = lazy(() => securePages().then(m => ({ default: m.ManageSpacesPage })));
const NotificationPreferencesPage = lazy(() => securePages().then(m => ({ default: m.NotificationPreferencesPage })));
const GoalDetailPage = lazy(() => securePages().then(m => ({ default: m.GoalDetailPage })));
const GoalsPage = lazy(() => securePages().then(m => ({ default: m.GoalsPage })));
const RecurringDetailPage = lazy(() => securePages().then(m => ({ default: m.RecurringDetailPage })));
const RecurringPage = lazy(() => securePages().then(m => ({ default: m.RecurringPage })));
const RecurringReviewPage = lazy(() => securePages().then(m => ({ default: m.RecurringReviewPage })));
const ProfilePage = lazy(() => securePages().then(m => ({ default: m.ProfilePage })));
const SettingsPage = lazy(() => securePages().then(m => ({ default: m.SettingsPage })));
const SpendingReportPage = lazy(() => securePages().then(m => ({ default: m.SpendingReportPage })));
const SubscriptionPage = lazy(() => securePages().then(m => ({ default: m.SubscriptionPage })));
const TagsPageContainer = lazy(() => securePages().then(m => ({ default: m.TagsPageContainer })));
const TransactionsPage = lazy(() => securePages().then(m => ({ default: m.TransactionsPage })));

export const router = createBrowserRouter([
	{
		path: '/',
		element: <RootLayout />,
		errorElement: <ErrorPage />,
		// Shown only while the first loader runs, which is behind the native splash on iOS and Android.
		HydrateFallback: () => null,
		children: [
			{ index: true, loader: redirectSignedInUser, Component: LoginPage },
			{ path: 'login', loader: redirectSignedInUser, Component: LoginPage },
			{ path: 'register', Component: RegisterPage },
			{ path: 'forgot-password', Component: ForgotPasswordPage },
			{ path: 'reset-password', Component: ResetPasswordPage },
			{ path: 'verify/:code', Component: EmailValidationPage },
			{ path: 'invitation/:code', Component: InvitationPage },
			{ path: 'logout', Component: LogoutPage },
			{ path: 'no-space', Component: NoSpacePage },
			{ path: 'help', Component: HelpPage },
			{
				element: <SiteLayout />, // TODO: Review this. SiteLayout already has a SecureRoute. Do we need one here too?
				children: [
					{
						path: 'dashboard',
						element: (
							<SecureRoute>
								<DashboardPage />
							</SecureRoute>
						),
						errorElement: <ErrorAlert />,
					},
					// budgets
					{
						path: 'budgets',
						element: (
							<SecureRoute>
								<BudgetsPage />
							</SecureRoute>
						),
						errorElement: <ErrorAlert />,
					},
					// goals
					...[
						{ path: 'goals', Page: GoalsPage },
						{ path: 'goals/:id', Page: GoalDetailPage },
					].map(({ path, Page }) => ({
						path,
						element: (
							<SecureRoute>
								<Page />
							</SecureRoute>
						),
						errorElement: <ErrorAlert />,
					})),
					// recurring
					...[
						{ path: 'recurring', Page: RecurringPage },
						{ path: 'recurring/review', Page: RecurringReviewPage },
						{ path: 'recurring/:id', Page: RecurringDetailPage },
					].map(({ path, Page }) => ({
						path,
						element: (
							<SecureRoute>
								<Page />
							</SecureRoute>
						),
						errorElement: <ErrorAlert />,
					})),
					// investments
					{
						path: 'investments',
						element: (
							<SecureRoute>
								<InvestmentsPage />
							</SecureRoute>
						),
						errorElement: <ErrorAlert />,
					},
					// transactions
					{
						path: 'transactions',
						element: (
							<SecureRoute>
								<TransactionsPage />
							</SecureRoute>
						),
						errorElement: <ErrorAlert />,
					},
					// accounts
					{
						path: 'accounts',
						element: (
							<SecureRoute>
								<AccountsPage />
							</SecureRoute>
						),
						errorElement: <ErrorAlert />,
					},
					// reports
					{
						path: 'spending',
						element: (
							<SecureRoute>
								<SpendingReportPage />
							</SecureRoute>
						),
						errorElement: <ErrorAlert />,
					},
					{
						path: 'cashflow',
						element: (
							<SecureRoute>
								<CashflowReportPage />
							</SecureRoute>
						),
						errorElement: <ErrorAlert />,
					},
					{
						path: 'assistant',
						element: (
							<SecureRoute>
								<AssistantRouteGate>
									<AssistantPage />
								</AssistantRouteGate>
							</SecureRoute>
						),
						errorElement: <ErrorAlert />,
					},
					// settings
					{
						path: 'settings',
						element: <SettingsPage />,
						children: [
							{
								index: true,
								path: 'profile',
								element: (
									<SecureRoute>
										<ProfilePage />
									</SecureRoute>
								),
								errorElement: <ErrorAlert />,
							},
							{
								path: 'manage-accounts',
								element: (
									<SecureRoute>
										<ManageAccountsPage />
									</SecureRoute>
								),
								errorElement: <ErrorAlert />,
							},
							{
								path: 'institutions',
								element: (
									<SecureRoute>
										<InstitutionsPage />
									</SecureRoute>
								),
								errorElement: <ErrorAlert />,
							},
							{
								path: 'categories',
								element: (
									<SecureRoute>
										<ManageCategoriesPage />
									</SecureRoute>
								),
								errorElement: <ErrorAlert />,
							},
							{
								path: 'rules',
								element: (
									<SecureRoute>
										<ManageRulesPage />
									</SecureRoute>
								),
								errorElement: <ErrorAlert />,
							},
							{
								path: 'tags',
								element: (
									<SecureRoute>
										<TagsPageContainer />
									</SecureRoute>
								),
								errorElement: <ErrorAlert />,
							},
							{
								path: 'notifications',
								element: (
									<SecureRoute>
										<NotificationPreferencesPage />
									</SecureRoute>
								),
								errorElement: <ErrorAlert />,
							},
							{
								path: 'all-spaces',
								element: (
									<SecureRoute>
										<ManageSpacesPage />
									</SecureRoute>
								),
								errorElement: <ErrorAlert />,
							},
							{
								path: 'subscription',
								element: (
									<SecureRoute>
										<SubscriptionPage />
									</SecureRoute>
								),
								errorElement: <ErrorAlert />,
							},
						],
					},
				],
			},
		],
	},
]);
